import React, { useEffect, useRef, useState } from 'react';
import { AppHeader, Button, Dialog, Notice } from '@origo/origo-ui';
import { CheckIcon } from '@phosphor-icons/react';
import { ApiError } from '../../../api/client';
import { useCreateBinding, usePreflight } from '../../../api/hooks';
import type {
    AgolLayerInfo,
    PreflightReport,
    PreflightRequest,
    SyncBinding,
} from '../../../api/types';
import { toBindingId } from '../bindingId';
import {
    type FieldDraft,
    type LayerDraft,
    buildMappingProfile,
    draftFromReport,
    layersUsedBy,
    mappingComplete,
    mappingProblem,
    reassignLayer,
    resolveLokalIdField,
} from './draft';
import { CreateServiceStep } from './CreateServiceStep';
import { LayerList } from './LayerList';
import { LayerMappingPanel } from './LayerMappingPanel';
import { CreateConfirmStep, type CreateMetadata } from './CreateConfirmStep';
import styles from './create.module.css';

interface CreateBindingFlowProps {
    open: boolean;
    defaultSystem: string;
    onClose: () => void;
    onCreated: (binding: SyncBinding) => void;
}

type Step = 'service' | 'mapping' | 'confirm';

const STEPS: Step[] = ['service', 'mapping', 'confirm'];

const STEP_LABELS: Record<Step, string> = {
    service: 'Tjeneste',
    mapping: 'Mapping',
    confirm: 'Bekreft',
};

/** Signatur av tjeneste-inputene en preflight-rapport ble bygd fra (for å unngå re-kjøring). */
function serviceSignature(request: PreflightRequest): string {
    return JSON.stringify([
        request.forvaltningssystem.trim(),
        request.centralDatasetId.trim(),
        request.agolBaseUrl.trim(),
    ]);
}

function errorMessage(error: unknown, fallback: string): string | null {
    if (error instanceof ApiError) return error.message;
    return error ? fallback : null;
}

/**
 * Fullskjerm-takeover for å opprette en kobling.
 *
 * Det finnes ÉN bindingstype, og dermed én flyt: tjeneste → mapping → bekreft. Preflight
 * inspiserer tjenesten og foreslår en felt-mapping som brukeren ser over og korrigerer før
 * den lagres. Er tjenesten provisjonert av oss, matcher alt på navn og mapping-steget blir
 * bare en bekreftelse — men det er samme steg og samme datastruktur.
 *
 * Speiler dialog-takeover-mønsteret fra drømmeplan-admin (SectionEditorPage).
 */
export const CreateBindingFlow: React.FC<CreateBindingFlowProps> = ({
    open,
    defaultSystem,
    onClose,
    onCreated,
}) => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const preflight = usePreflight();
    const createBinding = useCreateBinding();

    /** Tjeneste-signaturen `report` ble bygd fra — re-kjør preflight bare ved endring. */
    const [reportSignature, setReportSignature] = useState<string | null>(null);
    const [step, setStep] = useState<Step>('service');
    const [request, setRequest] = useState<PreflightRequest>({
        forvaltningssystem: defaultSystem,
        centralDatasetId: '',
        agolBaseUrl: '',
    });
    const [report, setReport] = useState<PreflightReport | null>(null);
    const [layers, setLayers] = useState<LayerDraft[]>([]);
    const [availableAgolLayers, setAvailableAgolLayers] = useState<AgolLayerInfo[]>([]);
    const [metadata, setMetadata] = useState<CreateMetadata>({
        bindingId: '',
        displayName: '',
        enabled: true,
        directionPolicy: null,
    });
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [confirmCancel, setConfirmCancel] = useState(false);

    // Synk dialog open/close mot <dialog>.
    useEffect(() => {
        const el = dialogRef.current;
        if (!el) return;
        if (open) {
            if (!el.open) el.showModal();
        } else if (el.open) {
            el.close();
        }
    }, [open]);

    // Nullstill tilstand når flyten åpnes.
    useEffect(() => {
        if (!open) return;
        setReportSignature(null);
        setStep('service');
        setRequest({ forvaltningssystem: defaultSystem, centralDatasetId: '', agolBaseUrl: '' });
        setReport(null);
        setLayers([]);
        setAvailableAgolLayers([]);
        setMetadata({ bindingId: '', displayName: '', enabled: true, directionPolicy: null });
        setSelectedIndex(null);
        setConfirmCancel(false);
        preflight.reset();
        createBinding.reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, defaultSystem]);

    const hasProgress =
        step !== 'service' ||
        report !== null ||
        !!request.centralDatasetId.trim() ||
        !!request.agolBaseUrl.trim();

    const requestCancel = () => {
        if (hasProgress) setConfirmCancel(true);
        else onClose();
    };

    // ESC: ikke lukk uten bekreftelse når noe er fylt ut.
    useEffect(() => {
        const el = dialogRef.current;
        if (!el) return;
        const handleCancel = (e: Event) => {
            e.preventDefault();
            requestCancel();
        };
        el.addEventListener('cancel', handleCancel);
        return () => el.removeEventListener('cancel', handleCancel);
    });

    /**
     * Kjører preflight og bygger mapping-utkastet. Kaster ved feil (vises via preflight.error
     * på tjeneste-steget).
     */
    const runPreflight = async (): Promise<void> => {
        const result = await preflight.mutateAsync({
            forvaltningssystem: request.forvaltningssystem.trim(),
            centralDatasetId: request.centralDatasetId.trim(),
            agolBaseUrl: request.agolBaseUrl.trim(),
        });
        setReport(result);
        setLayers(draftFromReport(result));
        setAvailableAgolLayers(result.availableAgolLayers ?? []);
        setSelectedIndex(result.layers.length > 0 ? 0 : null);
        setMetadata((m) => ({
            ...m,
            bindingId: m.bindingId || toBindingId(result.layers[0]?.featureType ?? ''),
            displayName: m.displayName || (result.layers[0]?.featureType ?? ''),
        }));
        setReportSignature(serviceSignature(request));
    };

    const updateField = (layerIndex: number, ngisSourcePath: string, patch: Partial<FieldDraft>) => {
        setLayers((prev) =>
            prev.map((layer, i) =>
                i === layerIndex
                    ? {
                          ...layer,
                          fields: layer.fields.map((f) =>
                              f.ngisSourcePath === ngisSourcePath ? { ...f, ...patch } : f,
                          ),
                      }
                    : layer,
            ),
        );
    };

    const reassign = (layerIndex: number, target: AgolLayerInfo | null) => {
        setLayers((prev) =>
            prev.map((layer, i) => (i === layerIndex ? reassignLayer(layer, target) : layer)),
        );
    };

    const submit = async () => {
        const bindingId = metadata.bindingId.trim();
        try {
            const saved = await createBinding.mutateAsync({
                bindingId,
                displayName: metadata.displayName.trim(),
                forvaltningssystem: request.forvaltningssystem.trim(),
                centralDatasetId: request.centralDatasetId.trim(),
                agolBaseUrl: request.agolBaseUrl.trim(),
                enabled: metadata.enabled,
                directionPolicy: metadata.directionPolicy,
                // Utledet fra mappingen: API-et bruker feltet som AGOL-kolonne ved oppslag
                // på lokalId, så det må være samme felt som profilen mapper.
                lokalIdField: resolveLokalIdField(layers),
                mappingProfile: buildMappingProfile(bindingId, layers),
            });
            onCreated(saved);
        } catch {
            /* feil vises via createBinding.error */
        }
    };

    // --- Footer-navigasjon ---
    const serviceReady = !!(
        request.forvaltningssystem.trim() &&
        request.centralDatasetId.trim() &&
        request.agolBaseUrl.trim()
    );
    const canSubmit =
        !!metadata.bindingId.trim() &&
        !!metadata.displayName.trim() &&
        metadata.directionPolicy != null &&
        !createBinding.isPending;

    const goNext = async () => {
        if (step === 'service') {
            // Gjenbruk eksisterende rapport hvis tjeneste-inputene er uendret.
            if (!report || reportSignature !== serviceSignature(request)) {
                try {
                    await runPreflight();
                } catch {
                    /* feil vises via preflight.error på tjeneste-steget */
                    return;
                }
            }
            setStep('mapping');
            return;
        }
        if (step === 'mapping') setStep('confirm');
    };
    const goBack = () => {
        if (step === 'confirm') setStep('mapping');
        else if (step === 'mapping') setStep('service');
    };

    const selectedLayer = selectedIndex !== null ? layers[selectedIndex] : null;

    const footer = (
        <div className={styles.footer}>
            <Button variant="ghost" onClick={requestCancel} disabled={createBinding.isPending}>
                Avbryt
            </Button>
            <div className={styles.footerEnd}>
                {step !== 'service' && (
                    <Button variant="secondary" onClick={goBack} disabled={createBinding.isPending}>
                        Tilbake
                    </Button>
                )}
                {step === 'confirm' ? (
                    <Button
                        variant="primary"
                        onClick={submit}
                        disabled={!canSubmit}
                        leftIcon={<CheckIcon size={16} weight="bold" />}
                    >
                        {createBinding.isPending ? 'Oppretter…' : 'Opprett kobling'}
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        onClick={goNext}
                        disabled={
                            (step === 'service' && (!serviceReady || preflight.isPending)) ||
                            (step === 'mapping' && !mappingComplete(layers))
                        }
                    >
                        {step === 'service' && preflight.isPending
                            ? 'Inspiserer tjeneste…'
                            : 'Videre'}
                    </Button>
                )}
            </div>
        </div>
    );

    return (
        <dialog ref={dialogRef} className={styles.page} aria-label="Opprett kobling">
            <AppHeader title="Opprett kobling" onBack={requestCancel} backLabel="Koblinger" />

            <div className={styles.steps}>
                {STEPS.map((s, i) => {
                    const activeIdx = STEPS.indexOf(step);
                    const state = s === step ? 'active' : i < activeIdx ? 'done' : 'todo';
                    return (
                        <React.Fragment key={s}>
                            {i > 0 && <span className={styles.stepSep}>›</span>}
                            <span
                                className={`${styles.step} ${
                                    state === 'active'
                                        ? styles.stepActive
                                        : state === 'done'
                                          ? styles.stepDone
                                          : ''
                                }`}
                            >
                                <span className={styles.stepIndex}>{i + 1}</span>
                                {STEP_LABELS[s]}
                            </span>
                        </React.Fragment>
                    );
                })}
            </div>

            <div className={styles.content}>
                {step === 'service' && (
                    <div className={styles.singleScroll}>
                        <CreateServiceStep
                            request={request}
                            onChange={(patch) => setRequest((r) => ({ ...r, ...patch }))}
                            preflightError={errorMessage(
                                preflight.error,
                                'Preflight feilet. Sjekk URL og token.',
                            )}
                        />
                    </div>
                )}

                {step === 'mapping' && (
                    <>
                        <div className={styles.listPane}>
                            <LayerList
                                layers={layers}
                                selectedIndex={selectedIndex}
                                onSelect={setSelectedIndex}
                            />
                        </div>
                        <div className={styles.editorPane}>
                            {mappingProblem(layers) && (
                                <Notice variant="warning">{mappingProblem(layers)}</Notice>
                            )}
                            {selectedLayer ? (
                                <LayerMappingPanel
                                    layer={selectedLayer}
                                    availableAgolLayers={availableAgolLayers}
                                    usedBy={layersUsedBy(layers, selectedIndex)}
                                    onLayerChange={(target) => reassign(selectedIndex!, target)}
                                    onFieldChange={(path, patch) =>
                                        updateField(selectedIndex!, path, patch)
                                    }
                                />
                            ) : (
                                <div className={styles.placeholder}>
                                    <p className={styles.muted}>
                                        Velg en objekttype i listen til venstre for å redigere
                                        mappingen.
                                    </p>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {step === 'confirm' && (
                    <div className={styles.singleScroll}>
                        <CreateConfirmStep
                            metadata={metadata}
                            onMetaChange={(patch) => setMetadata((m) => ({ ...m, ...patch }))}
                            request={request}
                            layers={layers}
                            report={report}
                            error={errorMessage(
                                createBinding.error,
                                'Lagring feilet. Sjekk mapping og token.',
                            )}
                        />
                    </div>
                )}
            </div>

            {footer}

            <Dialog
                open={confirmCancel}
                onClose={() => setConfirmCancel(false)}
                title="Forkast opprettelsen?"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setConfirmCancel(false)}>
                            Fortsett
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => {
                                setConfirmCancel(false);
                                onClose();
                            }}
                        >
                            Forkast
                        </Button>
                    </>
                }
            >
                <p>Du har påbegynt en kobling. Lukker du nå, går det du har fylt inn tapt.</p>
            </Dialog>
        </dialog>
    );
};
